import type { Translation } from '@teamhanko/hanko-elements';

export const es: Translation = {
    headlines: {
        error: 'Se ha producido un error',
        accountNotFound: 'Cuenta no encontrada',
        authenticatorApp: 'Aplicación de autenticación',
        authenticatorAppAlreadySetUp: 'La aplicación de autenticación está configurada',
        authenticatorAppNotSetUp: 'Configurar la aplicación de autenticación',
        loginEmail: 'Iniciar sesión o crear una cuenta',
        loginEmailNoSignup: 'Iniciar sesión',
        loginFinished: 'Inicio de sesión correcto',
        loginPasscode: 'Introduce el código de acceso',
        loginPassword: 'Introduce la contraseña',
        registerAuthenticator: 'Crear una clave de acceso',
        registerConfirm: '¿Crear una cuenta?',
        registerPassword: 'Establecer una contraseña nueva',
        securityKeyLogin: 'Clave de seguridad',
        securityKeySetUp: 'Añadir una clave de seguridad',
        trustDevice: '¿Confiar en este navegador?',
        mfaSetUp: 'Configurar la autenticación multifactor',
        otpLogin: 'Código de autenticación',
        otpSetUp: 'Configurar la aplicación de autenticación',
        profileEmails: 'Correos electrónicos',
        profilePassword: 'Contraseña',
        profilePasskeys: 'Claves de acceso',
        isPrimaryEmail: 'Dirección de correo principal',
        setPrimaryEmail: 'Establecer como dirección de correo principal',
        createEmail: 'Introduce un correo electrónico nuevo',
        createUsername: 'Introduce un nombre de usuario nuevo',
        emailVerified: 'Verificado',
        emailUnverified: 'No verificado',
        emailDelete: 'Eliminar',
        renamePasskey: 'Cambiar el nombre de la clave de acceso',
        deletePasskey: 'Eliminar la clave de acceso',
        lastUsedAt: 'Último uso',
        createdAt: 'Creado el',
        connectedAccounts: 'Cuentas conectadas',
        deleteAccount: 'Eliminar la cuenta',
        signIn: 'Iniciar sesión',
        signUp: 'Crear una cuenta',
        selectLoginMethod: 'Selecciona un método de inicio de sesión',
        setupLoginMethod: 'Configura un método de inicio de sesión',
        lastUsed: 'Último acceso',
        ipAddress: 'Dirección IP',
        revokeSession: 'Revocar la sesión',
        profileSessions: 'Sesiones',
        renameSecurityKey: 'Cambiar el nombre de la clave de seguridad',
        deleteSecurityKey: 'Eliminar la clave de seguridad',
        securityKeys: 'Claves de seguridad',
        deleteIdentity: 'Eliminar la conexión',
    },
    texts: {
        authenticatorAppAlreadySetUp:
            'Tu cuenta está protegida con una aplicación de autenticación que genera contraseñas de un solo uso basadas en el tiempo (TOTP) para la autenticación multifactor.',
        authenticatorAppNotSetUp:
            'Protege tu cuenta con una aplicación de autenticación que genera contraseñas de un solo uso basadas en el tiempo (TOTP) para la autenticación multifactor.',
        enterPasscode: 'Introduce el código de acceso enviado a tu dirección de correo electrónico.',
        enterPasscodeNoEmail: 'Introduce el código de acceso enviado a tu dirección de correo principal.',
        setupPasskey:
            'Inicia sesión en tu cuenta de forma fácil y segura con una clave de acceso. Nota: tus datos biométricos solo se almacenan en tus dispositivos y nunca se comparten con nadie.',
        createAccount: 'No existe ninguna cuenta para «{emailAddress}». ¿Quieres crear una cuenta nueva?',
        mfaSetUp:
            'Protege tu cuenta con la autenticación multifactor (MFA). La MFA añade un paso adicional al inicio de sesión para mantener tu cuenta segura aunque tu contraseña o tu correo electrónico estén en riesgo.',
        noAccountExists: 'No existe ninguna cuenta para «{emailAddress}».',
        otpEnterVerificationCode:
            'Introduce abajo la contraseña de un solo uso (OTP) obtenida de tu aplicación de autenticación:',
        otpLogin:
            'Abre tu aplicación de autenticación para obtener la contraseña de un solo uso (OTP). Introduce el código en el campo siguiente para completar el inicio de sesión.',
        otpScanQRCode:
            'Escanea el código QR con tu aplicación de autenticación, como Google Authenticator u otra aplicación TOTP. También puedes introducir manualmente la clave secreta OTP en la aplicación.',
        otpSecretKey: 'Clave secreta OTP',
        passwordFormatHint: 'Debe tener entre {minLength} y {maxLength} caracteres.',
        securityKeyLogin:
            'Conecta o activa tu clave de seguridad y haz clic en el botón siguiente. Cuando esté lista, úsala mediante USB, NFC o tu teléfono móvil. Sigue las instrucciones para completar el inicio de sesión.',
        trustDevice:
            'Si confías en este navegador, no tendrás que introducir tu OTP (contraseña de un solo uso) ni usar tu clave de seguridad para la autenticación multifactor (MFA) la próxima vez que inicies sesión.',
        isPrimaryEmail: 'Esta dirección de correo se usará para ponerse en contacto contigo si es necesario.',
        securityKeySetUp:
            'Usa una clave de seguridad específica mediante USB, Bluetooth o NFC, o tu teléfono móvil. Conecta o activa la clave de seguridad, haz clic en el botón siguiente y sigue las instrucciones para completar el registro.',
        setPrimaryEmail: 'Establece esta dirección de correo para que se use para ponerse en contacto contigo.',
        emailVerified: 'Esta dirección de correo ha sido verificada.',
        emailUnverified: 'Esta dirección de correo no ha sido verificada.',
        emailDelete: 'Si eliminas esta dirección de correo, ya no podrás usarla para iniciar sesión.',
        renamePasskey: 'Define un nombre para la clave de acceso.',
        deletePasskey: 'Elimina esta clave de acceso de tu cuenta.',
        deleteAccount: '¿Seguro que quieres eliminar esta cuenta? Todos los datos se eliminarán de inmediato y no se podrán recuperar.',
        selectLoginMethodForFutureLogins:
            'Selecciona uno de los siguientes métodos de inicio de sesión para usarlo en futuros inicios de sesión.',
        howDoYouWantToLogin: '¿Cómo quieres iniciar sesión?',
        deleteSecurityKey: 'Elimina esta clave de seguridad de tu cuenta.',
        renameSecurityKey: 'Define un nombre para la clave de seguridad.',
    },
    labels: {
        authenticatorApp: 'Aplicación de autenticación',
        authenticatorAppAdd: 'Configurar',
        authenticatorAppManage: 'Administrar la aplicación de autenticación',
        or: 'o',
        no: 'no',
        yes: 'sí',
        email: 'Correo electrónico',
        continue: 'Continuar',
        copied: 'copiado',
        configured: 'configurado',
        skip: 'Omitir',
        save: 'Guardar',
        password: 'Contraseña',
        passkey: 'Clave de acceso',
        passcode: 'Código de acceso',
        signInPassword: 'Iniciar sesión con una contraseña',
        signInPasscode: 'Iniciar sesión con un código de acceso',
        forgotYourPassword: '¿Has olvidado tu contraseña?',
        back: 'Atrás',
        registerAuthenticator: 'Crear una clave de acceso',
        securityKey: 'Clave de seguridad',
        securityKeyUse: 'Usar la clave de seguridad',
        signIn: 'Iniciar sesión',
        signInPasskey: 'Iniciar sesión con una clave de acceso',
        signUp: 'Crear una cuenta',
        sendNewPasscode: 'Enviar un código nuevo',
        staySignedIn: 'Mantener la sesión iniciada',
        trustDevice: 'Confiar en este navegador',
        passwordRetryAfter: 'Volver a intentarlo en {passwordRetryAfter}',
        passcodeResendAfter: 'Solicitar un código nuevo en {passcodeResendAfter}',
        useAnotherMethod: 'Usar otro método',
        unverifiedEmail: 'no verificado',
        primaryEmail: 'principal',
        setAsPrimaryEmail: 'Establecer como principal',
        verify: 'Verificar',
        delete: 'Eliminar',
        newEmailAddress: 'Nueva dirección de correo',
        newPassword: 'Nueva contraseña',
        rename: 'Cambiar el nombre',
        newPasskeyName: 'Nombre nuevo de la clave de acceso',
        addEmail: 'Añadir un correo electrónico',
        createPasskey: 'Crear una clave de acceso',
        webauthnUnsupported: 'Tu navegador no admite claves de acceso',
        signInWith: 'Continuar con {provider}',
        deleteAccount: 'Sí, eliminar esta cuenta.',
        emailOrUsername: 'Correo electrónico o nombre de usuario',
        username: 'Nombre de usuario',
        optional: 'opcional',
        dontHaveAnAccount: '¿No tienes una cuenta?',
        alreadyHaveAnAccount: '¿Ya tienes una cuenta?',
        changePassword: 'Cambiar la contraseña',
        setPassword: 'Establecer la contraseña',
        changeUsername: 'Cambiar el nombre de usuario',
        setUsername: 'Establecer el nombre de usuario',
        revoke: 'Revocar',
        currentSession: 'Sesión actual',
        newSecurityKeyName: 'Nombre nuevo de la clave de seguridad',
        createSecurityKey: 'Añadir una clave de seguridad',
        lastUsed: 'Último uso',
        connectAccount: 'Conectar una cuenta',
    },
    errors: {
        somethingWentWrong: 'Se ha producido un error técnico. Inténtalo de nuevo más tarde.',
        requestTimeout: 'La solicitud ha agotado el tiempo de espera.',
        invalidPassword: 'Correo electrónico o contraseña incorrectos.',
        invalidPasscode: 'El código de acceso no es correcto.',
        passcodeAttemptsReached: 'Has introducido el código de acceso incorrectamente demasiadas veces. Solicita un código nuevo.',
        tooManyRequests: 'Se han realizado demasiadas solicitudes. Espera antes de repetir la operación.',
        unauthorized: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
        invalidWebauthnCredential: 'Esta clave de acceso ya no se puede usar.',
        passcodeExpired: 'El código de acceso ha caducado. Solicita uno nuevo.',
        userVerification: 'Se requiere la verificación del usuario. Asegúrate de que tu dispositivo de autenticación está protegido con un PIN o datos biométricos.',
        emailAddressAlreadyExistsError: 'La dirección de correo ya existe.',
        maxNumOfEmailAddressesReached: 'No se pueden añadir más direcciones de correo.',
        thirdPartyAccessDenied: 'Acceso denegado. Has cancelado la solicitud o el proveedor ha denegado el acceso por otro motivo.',
        thirdPartyMultipleAccounts: 'No se puede identificar la cuenta. La dirección de correo se usa en varias cuentas.',
        thirdPartyUnverifiedEmail: 'Se requiere verificar el correo electrónico. Verifica la dirección usada con tu proveedor.',
        signupDisabled: 'El registro de cuentas está desactivado.',
        handlerNotFoundError: 'Esta versión de la aplicación no admite el paso actual. Inténtalo de nuevo más tarde o contacta con soporte si el problema continúa.',
    },
    flowErrors: {
        technical_error: 'Se ha producido un error técnico. Inténtalo de nuevo más tarde.',
        flow_expired_error: 'La sesión ha caducado. Haz clic en el botón para volver a empezar.',
        value_invalid_error: 'El valor introducido no es válido.',
        passcode_invalid: 'El código de acceso no es correcto.',
        passkey_invalid: 'Esta clave de acceso ya no se puede usar.',
        passcode_max_attempts_reached: 'Has introducido el código de acceso incorrectamente demasiadas veces. Solicita un código nuevo.',
        rate_limit_exceeded: 'Se han realizado demasiadas solicitudes. Espera antes de repetir la operación.',
        unknown_username_error: 'Se desconoce el nombre de usuario.',
        unknown_email_error: 'Se desconoce la dirección de correo.',
        username_already_exists: 'El nombre de usuario ya está en uso.',
        invalid_username_error: 'El nombre de usuario solo puede contener letras, números y guiones bajos.',
        email_already_exists: 'El correo electrónico ya está en uso.',
        not_found: 'No se ha encontrado el recurso solicitado.',
        flow_discontinuity_error: 'No se puede continuar el proceso debido a la configuración del usuario o del proveedor.',
        operation_not_permitted_error: 'La operación no está permitida.',
        platform_authenticator_required: 'Tu cuenta está configurada para usar autenticadores de plataforma, pero tu dispositivo o navegador actual no admite esta función. Inténtalo con un dispositivo o navegador compatible.',
        form_data_invalid_error: 'Los datos enviados en el formulario contienen errores.',
        unauthorized: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
        value_missing_error: 'Falta el valor.',
        value_too_long_error: 'El valor es demasiado largo.',
        value_too_short_error: 'El valor es demasiado corto.',
        webauthn_credential_invalid_mfa_only: 'Esta credencial solo se puede usar como clave de seguridad del segundo factor.',
        webauthn_credential_already_exists: 'La solicitud ha agotado el tiempo, se ha cancelado o el dispositivo ya está registrado. Inténtalo de nuevo o usa otro dispositivo.',
        third_party_access_denied: 'Acceso denegado. Has cancelado la solicitud o el proveedor ha denegado el acceso por otro motivo.',
    },
};
